import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import AiChatBody from "../AiChatComponent/panelComponents/AiChatBody";
import AiChatInput from "../AiChatComponent/panelComponents/AiChatInput";
import ConnectionStatusBanner from "../AiChatComponent/panelComponents/ConnectionStatusBanner";
import ModalBase from "./ModalBase";
import useAtMention, { MentionedAsset } from "../AiChatComponent/hooks/useAtMention";
import useChatScroll from "../AiChatComponent/hooks/useChatScroll";
import { MCPService, AttachedFile } from "../../services/mcpService";
import { ConversationService } from "../../services/conversationService";
import { useVisibilityRefetch } from "../../hooks/useVisibilityRefetch";
import {
  selectProjectConversation,
  sendProjectMessage,
  setProjectInputValue,
  clearProjectChat,
  handleProjectMessageTimeout,
  uploadProjectFile,
  removeProjectAttachedFile,
  setProjectAttachedFiles,
} from "../../store/projectChatSlice";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { buildAssetContext } from "../../utils/assetContext";
import type { AppDispatch, RootState } from "../../store";

interface ProjectChatModalProps {
  onClose: (result?: unknown) => void;
  projectId: string;
  projectTitle: string;
  initialConversationId?: string;
  initialInputValue?: string;
  projectTaskId?: string;
  // Already-uploaded files carried across the map-draw handoff (which unmounts this modal)
  initialAttachedFiles?: AttachedFile[];
  // Result of a modal we handed off to (via __returnTo) that just closed — e.g. the map-draw KML
  returnedResult?: { type?: string; kmlContent?: string; filename?: string };
}

const ProjectChatModal: React.FC<ProjectChatModalProps> = ({ onClose, projectId, projectTitle, initialConversationId, initialInputValue, projectTaskId, initialAttachedFiles, returnedResult }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { messages, messagesLoading, currentConversation, inputValue, isSending, sendError, pendingSendTimestamp, tasks, attachedFiles, uploadingCount, pendingConversationId } =
    useSelector((state: RootState) => state.projectChat);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mapKmlAttachedRef = useRef(false);

  const {
    mentionedAssets,
    isPickerOpen: isMentionPickerOpen,
    mentionQuery,
    filteredAssets: mentionSuggestions,
    handleInputChange: mentionHandleInputChange,
    selectAsset,
    removeMention,
    clearMentions,
    closePicker,
    restoreMentions,
    resolveMentionsFromText,
  } = useAtMention();
  const mentionsRestoredRef = useRef(false);
  const mentionedAssetsRef = useRef(mentionedAssets);
  mentionedAssetsRef.current = mentionedAssets;

  useEffect(() => {
    MCPService.warmup();
  }, []);

  useEffect(() => {
    if (initialConversationId) {
      dispatch(selectProjectConversation(initialConversationId));
    } else {
      dispatch(clearProjectChat());
    }
    // Restore an in-progress draft (e.g. carried across the map-draw handoff)
    if (initialInputValue) {
      dispatch(setProjectInputValue(initialInputValue));
    }
    // Restore files attached before the handoff — clearProjectChat above wiped them
    if (initialAttachedFiles?.length) {
      dispatch(setProjectAttachedFiles(initialAttachedFiles));
    }
    return () => { dispatch(clearProjectChat()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild @mention chips from the restored draft text once the asset list has loaded —
  // the handoff carries the tokens (in initialInputValue) but not the structured assets.
  useEffect(() => {
    if (mentionsRestoredRef.current || !initialInputValue?.includes("@")) return;
    const derived = resolveMentionsFromText(initialInputValue);
    if (derived.length) {
      restoreMentions(derived);
      mentionsRestoredRef.current = true;
    }
  }, [initialInputValue, resolveMentionsFromText, restoreMentions]);

  // Mark conversation as read on backend when one is loaded
  useEffect(() => {
    if (currentConversation?.id) {
      ConversationService.markConversationAsRead(currentConversation.id).catch(() => {});
    }
  }, [currentConversation?.id]);

  // Scroll within the chat container only — raw scrollIntoView pushed the whole page up on markdown/math render
  const lastMessageIsUser = messages[messages.length - 1]?.isUser ?? false;
  useChatScroll(messagesEndRef, [messages, isSending], lastMessageIsUser);

  const handleRefetch = useCallback((id: string) => {
    dispatch(selectProjectConversation(id));
  }, [dispatch]);

  // Message timeout handling for project chat. The shared useMessageTimeout hook
  // is bound to the aiChat slice, so project chat drives its own timers off the
  // projectChat slice here: refetch at 90s to catch a missed message, error at 120s.
  const staleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRefetchedRef = useRef(false);
  const conversationId = currentConversation?.id;

  useEffect(() => {
    if (staleTimeoutRef.current) clearTimeout(staleTimeoutRef.current);
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);

    if (!isSending) {
      hasRefetchedRef.current = false;
      return;
    }

    if (pendingSendTimestamp) {
      const elapsed = Date.now() - new Date(pendingSendTimestamp).getTime();
      const staleRemaining = 90000 - elapsed; // 90s - refetch
      const errorRemaining = 120000 - elapsed; // 120s - error

      if (staleRemaining > 0 && !hasRefetchedRef.current) {
        staleTimeoutRef.current = setTimeout(() => {
          if (conversationId) {
            hasRefetchedRef.current = true;
            dispatch(selectProjectConversation(conversationId));
          }
        }, staleRemaining);
      }

      if (errorRemaining > 0) {
        errorTimeoutRef.current = setTimeout(() => {
          dispatch(handleProjectMessageTimeout());
        }, errorRemaining);
      } else {
        dispatch(handleProjectMessageTimeout());
      }
    }

    return () => {
      if (staleTimeoutRef.current) clearTimeout(staleTimeoutRef.current);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, [isSending, pendingSendTimestamp, conversationId, dispatch]);

  useVisibilityRefetch({
    currentConversationId: currentConversation?.id,
    onRefetch: handleRefetch,
  });

  // Upload each picked file for the active (or pending) conversation
  const handleFileSelect = useCallback((files: FileList) => {
    const conversationId = currentConversation?.id ?? pendingConversationId;
    Array.from(files).forEach((file) => {
      dispatch(uploadProjectFile({ file, conversationId }));
    });
  }, [dispatch, currentConversation?.id, pendingConversationId]);

  const handleRemoveFile = useCallback((uri: string) => {
    dispatch(removeProjectAttachedFile(uri));
  }, [dispatch]);
  
  const returnToSelf = useMemo(() => ({
    modalView: modalViewsConst.projectChat,
    properties: {
      __modalSize: "2xl",
      projectId,
      projectTitle,
      ...(currentConversation?.id && { initialConversationId: currentConversation.id }),
      ...(projectTaskId && { projectTaskId }),
      ...(inputValue && { initialInputValue: inputValue }),
      ...(attachedFiles.length > 0 && { initialAttachedFiles: attachedFiles }),
    },
  }), [projectId, projectTitle, currentConversation?.id, projectTaskId, inputValue, attachedFiles]);

  // Single-modal system has no stack, so opening map-draw would unmount this modal.
  // Carry a __returnTo payload so ModalGlobal reopens this chat on ANY close (confirm/cancel/esc),
  // attaching the drawn KML only when Confirm produced one.
  const handleMapDraw = useCallback(() => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.chatMapDraw,
      properties: {
        __modalSize: "2xl",
        __returnTo: returnToSelf,
      },
    }));
  }, [dispatch, returnToSelf]);

  // After the map-draw handoff reopens this modal, attach the drawn KML — but wait until an
  // existing conversation has loaded so the upload targets the right conversation id.
  useEffect(() => {
    if (returnedResult?.type !== "chatMapDraw" || !returnedResult?.kmlContent || mapKmlAttachedRef.current) return;
    if (initialConversationId && currentConversation?.id !== initialConversationId) return;
    mapKmlAttachedRef.current = true;
    const blob = new Blob([returnedResult.kmlContent], { type: "application/vnd.google-earth.kml+xml" });
    const file = new File([blob], returnedResult.filename ?? "drawn-geometry.kml", { type: "application/vnd.google-earth.kml+xml" });
    const dt = new DataTransfer();
    dt.items.add(file);
    handleFileSelect(dt.files);
  }, [returnedResult, initialConversationId, currentConversation?.id, handleFileSelect]);

  const handleSend = useCallback(() => {
    // Silently inject @mention asset context into the prompt, same as the analyst
    const context = buildAssetContext(mentionedAssetsRef.current);
    const promptOverride = context ? inputValue + context : undefined;
    clearMentions();
    dispatch(sendProjectMessage({ projectId, inputValue, projectTaskId, projectTitle, tasks, attachedFilesInput: attachedFiles, promptOverride }));
  }, [dispatch, projectId, inputValue, projectTaskId, projectTitle, tasks, attachedFiles, clearMentions]);

  const handleInputChange = useCallback((value: string) => {
    mentionHandleInputChange(value, inputRef.current);
    dispatch(setProjectInputValue(value));
  }, [dispatch, mentionHandleInputChange]);

  const handleMentionSelect = useCallback((asset: MentionedAsset) => {
    dispatch(setProjectInputValue(selectAsset(asset, inputValue, inputRef.current)));
  }, [dispatch, selectAsset, inputValue]);

  const handleMentionRemove = useCallback((assetId: string) => {
    dispatch(setProjectInputValue(removeMention(assetId, inputValue)));
  }, [dispatch, removeMention, inputValue]);

  const handleCursorMove = useCallback(() => {
    mentionHandleInputChange(inputValue, inputRef.current);
  }, [mentionHandleInputChange, inputValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // While the mention picker has results, it owns Enter/Arrow keys — suppress send
      if (isMentionPickerOpen && mentionSuggestions.length > 0 && (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, isMentionPickerOpen, mentionSuggestions.length]
  );

  return (
    <ModalBase
      title={currentConversation ? currentConversation.title : `${projectTitle} — New Chat`}
      icon={<BriefcaseIcon className="w-5 h-5" style={{ stroke: "var(--stroke-0)" }} />}
      onClose={() => onClose()}
      size="2xl"
      className="h-[90dvh]"
      footer={
        <div className="mx-auto w-full max-w-[760px]">
          <ConnectionStatusBanner />
          <AiChatInput
            inputRef={inputRef}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSend={handleSend}
            isLoading={isSending}
            error={sendError}
            containerClassName="px-0 pb-0"
            attachedFiles={attachedFiles}
            isUploading={uploadingCount > 0}
            onFileSelect={handleFileSelect}
            onRemoveFile={handleRemoveFile}
            onMapDraw={handleMapDraw}
            mentionedAssets={mentionedAssets}
            isMentionPickerOpen={isMentionPickerOpen}
            mentionQuery={mentionQuery}
            mentionSuggestions={mentionSuggestions}
            onMentionSelect={handleMentionSelect}
            onMentionRemove={handleMentionRemove}
            onMentionPickerClose={closePicker}
            onCursorMove={handleCursorMove}
          />
        </div>
      }
    >
      <AiChatBody
        messages={messages}
        isLoading={isSending}
        isFetchingConversation={messagesLoading === "pending"}
        messagesEndRef={messagesEndRef}
        messagesClassName="mx-auto w-full max-w-[1024px] space-y-3 pb-4"
        hideMessagesWhenEmpty={false}
        emptyStateVariant="analyst"
        conversationId={currentConversation?.id}
        scheduleRunnerId={currentConversation?.agentScheduleRunnerId ?? undefined}
        shareReturnTo={returnToSelf}
      />
    </ModalBase>
  );
};

export default ProjectChatModal;
